// The models an agent can run on, stored as config.llm.model (owner, 2026-10-17;
// shared/agent-models.json, which liveModels.test.ts holds this file to). Gemini
// 3.8 Live hears the caller and speaks itself. A text model runs the traditional
// engine: speech-to-text, that model, then a Gemini voice (config.voice.ttsModel).
// The editor's Model dropdown picks one; on a text model its Thinking level dropdown
// picks how long the model thinks before each reply (config.settings.thinkingLevel).
export const GEMINI_3_8_LIVE = "gemini-3.8-live";
export const TEXT_MODELS = ["gemini-3.5-flash-lite", "gemini-3.6-flash", "gemini-3.8-flash"] as const;
/** The Model dropdown's choices, in its order. */
export const AGENT_MODELS = [GEMINI_3_8_LIVE, ...TEXT_MODELS] as const;

export type TextModel = (typeof TEXT_MODELS)[number];
export type AgentModel = (typeof AGENT_MODELS)[number];

/** The text model the Model dropdown recommends (owner, 2026-10-21). A new agent still starts on 3.8 Live. */
export const DEFAULT_TEXT_MODEL: TextModel = "gemini-3.5-flash-lite";

/** The Gemini voices a text-model agent can speak with; read only on a text model. */
export const TTS_MODELS = ["gemini-3.8-flash-tts", "gemini-3.8-flash-lite-tts"] as const;
export type TtsModel = (typeof TTS_MODELS)[number];
/**
 * What a text model speaks with while none is stored, and what the Voice model dropdown
 * preselects (owner, 2026-10-22: Flash-Lite's first audio came in 0.534 s against 0.773 s,
 * and it costs a third less). A voice model an agent saved stays as it is.
 */
export const DEFAULT_TTS_MODEL: TtsModel = "gemini-3.8-flash-lite-tts";

export const MODEL_LABELS: Record<AgentModel, string> = {
  "gemini-3.8-live": "Gemini 3.8 Live (speech-to-speech)",
  "gemini-3.5-flash-lite": "Gemini 3.5 Flash-Lite",
  "gemini-3.6-flash": "Gemini 3.6 Flash",
  "gemini-3.8-flash": "Gemini 3.8 Flash (most accurate)",
};

/** A model as the Model dropdown names it: the recommended text model says so. */
export const modelLabel = (model: AgentModel): string =>
  MODEL_LABELS[model] + (model === DEFAULT_TEXT_MODEL ? " (recommended)" : "");

/** The line under the Model dropdown: what the engine it picks does. */
export const MODEL_HELP = {
  live: "One model hears the caller and speaks.",
  text: "Traditional engine: speech-to-text, then this text model, then a Gemini voice. Knowledge-base passages are used for one reply and then dropped.",
} as const;

export const TTS_MODEL_LABELS: Record<TtsModel, string> = {
  "gemini-3.8-flash-tts": "Gemini 3.8 Flash TTS",
  "gemini-3.8-flash-lite-tts": "Gemini 3.8 Flash-Lite TTS",
};

export const isTextModel = (model: unknown): model is TextModel => (TEXT_MODELS as readonly unknown[]).includes(model);

export const isTtsModel = (model: unknown): model is TtsModel => (TTS_MODELS as readonly unknown[]).includes(model);

/** A stored model as the dropdown shows it: anything but a text model is 3.8 Live, which the server stores. */
export const agentModelOf = (model: unknown): AgentModel => (isTextModel(model) ? model : GEMINI_3_8_LIVE);

/** A stored voice model as the dropdown shows it: a missing or unknown one is the default. */
export const ttsModelOf = (model: unknown): TtsModel => (isTtsModel(model) ? model : DEFAULT_TTS_MODEL);

/** How long a text model thinks before each reply (config.settings.thinkingLevel). */
export type ThinkingLevel = "minimal" | "low" | "medium" | "high";

/**
 * The levels each text model takes, least thinking first (owner, 2026-10-18;
 * shared/agent-models.json thinking_levels): an agent put on a text model starts
 * at its first, and the bot runs a level its model does not take at the first.
 * 3.8 Flash refuses "minimal", and 3.8 Live and the voice models take no level,
 * so the dropdown shows on a text model only.
 */
export const THINKING_LEVELS: Record<TextModel, readonly ThinkingLevel[]> = {
  "gemini-3.5-flash-lite": ["minimal", "low", "medium", "high"],
  "gemini-3.6-flash": ["minimal", "low", "medium", "high"],
  "gemini-3.8-flash": ["low", "medium", "high"],
};

const THINKING_LEVEL_LABELS: Record<ThinkingLevel, string> = { minimal: "Minimal", low: "Low", medium: "Medium", high: "High" };

/** A level as the dropdown offers it on this model: its first, the level an agent starts at, is the default. */
export const thinkingLabel = (model: TextModel, level: ThinkingLevel): string =>
  THINKING_LEVEL_LABELS[level] + (level === THINKING_LEVELS[model][0] ? " (default)" : "");

/**
 * The line under the dropdown. On 3.8 Flash the higher levels were slow to start
 * a reply (free keys, 2026-10-18: 7 to 55 s at Medium and High, against 2.4 s at Low).
 */
export const thinkingHelp = (model: TextModel): string =>
  "How long the model thinks before each reply. A higher level can answer hard questions better, and every reply starts later." +
  (model === "gemini-3.8-flash" ? " On Gemini 3.8 Flash, Medium and High can take many seconds to start a reply." : "");

/** A stored level as a text model's dropdown shows it: one the model takes, in any case, as itself; any other, or none, as its first, which the bot runs. */
export const thinkingLevelOf = (model: TextModel, level: unknown): ThinkingLevel => {
  const code = typeof level === "string" ? level.trim().toLowerCase() : "";
  return THINKING_LEVELS[model].find((each) => each === code) ?? THINKING_LEVELS[model][0];
};
